import { useState, useMemo, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Search, Clock, Calendar, Sparkles, ArrowLeft } from "lucide-react";
import { useSEO } from "../../lib/seo";
import { BLOG_ARTICLES, BLOG_CATEGORIES } from "../../data/seoContent";

const BLOG_API = `${process.env.REACT_APP_BACKEND_URL}/api/blog`;

function formatMonthYear(iso) {
  if (!iso) return "Feb 2026";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

// Lightweight inline-markdown renderer for Journal article paragraphs —
// supports **bold**, [text](url) links and ![alt](url) images, no new
// dependency added. Falls back to plain text for anything else.
function renderRichText(text) {
  if (!text) return null;
  const tokens = text.split(/(\*\*.+?\*\*|!\[.*?\]\(.*?\)|\[.*?\]\(.*?\))/g).filter(Boolean);
  return tokens.map((tok, i) => {
    const bold = tok.match(/^\*\*(.+)\*\*$/);
    if (bold) return <strong key={i} className="text-white font-medium">{bold[1]}</strong>;
    const img = tok.match(/^!\[(.*?)\]\((.*?)\)$/);
    if (img) return <img key={i} src={img[2]} alt={img[1]} className="w-full rounded-xl my-4 border border-line" />;
    const link = tok.match(/^\[(.*?)\]\((.*?)\)$/);
    if (link) return <a key={i} href={link[2]} className="text-gold underline hover:text-amber-200" target="_blank" rel="noopener noreferrer">{link[1]}</a>;
    return tok;
  });
}

// Normalizes a CMS-authored article (from MongoDB) into the same shape as
// the static `article()` helper output, so existing rendering is untouched.
function normalizeCmsArticle(a) {
  return {
    slug: a.slug,
    category: a.category,
    title: a.title,
    excerpt: a.excerpt,
    read: `${a.read_minutes || 5} min`,
    date: formatMonthYear(a.published_at),
    sections: a.sections || [],
    keywords: a.keywords,
    image: a.featured_image || "",
    author: a.author,
    fromCms: true,
  };
}

function useAllArticles() {
  const [cmsArticles, setCmsArticles] = useState([]);
  useEffect(() => {
    fetch(BLOG_API)
      .then((r) => r.json())
      .then((d) => setCmsArticles((d.articles || []).map(normalizeCmsArticle)))
      .catch(() => setCmsArticles([]));
  }, []);
  return useMemo(() => [...cmsArticles, ...BLOG_ARTICLES], [cmsArticles]);
}

/**
 * BlogIndexPage — /blog and /blog/category/:slug
 * Lists articles in a graphical, content-rich grid.
 */
export function BlogIndexPage({ onBook }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const allArticles = useAllArticles();

  useSEO({
    title: "Smart Home Journal — Guides, Insights, Case Studies",
    description:
      "The GenieX Journal — expert guides on home automation, smart security, lighting design, location insights and case studies for India's premium homes.",
    path: "/blog",
    keywords: "smart home blog India, home automation guide, GenieX journal",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "@id": "https://geniex.in/blog",
        name: "GenieX Journal",
        description:
          "Expert guides, insights and case studies on premium smart home automation in India.",
        url: "https://geniex.in/blog",
        inLanguage: "en-IN",
        isPartOf: { "@type": "WebSite", name: "GenieX", url: "https://geniex.in" },
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "All articles",
        numberOfItems: allArticles.length,
        itemListElement: allArticles.slice(0, 30).map((a, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `https://geniex.in/blog/${a.slug}`,
          name: a.title,
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://geniex.in/" },
          { "@type": "ListItem", position: 2, name: "Journal", item: "https://geniex.in/blog" },
        ],
      },
    ],
  });

  const filtered = useMemo(() => {
    let list = allArticles;
    if (cat !== "all") list = list.filter((a) => a.category === cat);
    if (q.trim()) {
      const k = q.toLowerCase();
      list = list.filter((a) => a.title.toLowerCase().includes(k) || a.excerpt.toLowerCase().includes(k));
    }
    return list;
  }, [q, cat, allArticles]);

  return (
    <main data-testid="blog-index" className="bg-[var(--gx-bg)] text-white">
      {/* HERO */}
      <section className="relative pt-28 lg:pt-36 pb-12 lg:pb-16 border-b border-line/70 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_-20%,rgba(212,175,55,0.16),transparent_55%)]" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10">
          <p className="eyebrow mb-4">GenieX Journal</p>
          <h1 className="font-display tracking-tight text-white text-4xl sm:text-5xl lg:text-[64px] leading-[1.04] max-w-[22ch]">
            Guides, insights and case studies for India&apos;s most thoughtful homes.
          </h1>

          {/* Filter bar */}
          <div className="mt-10 flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="relative flex-1 max-w-[420px]">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-mute" />
              <input
                data-testid="blog-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search articles…"
                className="w-full bg-transparent border border-line focus:border-gold/60 outline-none rounded-full pl-11 pr-5 py-3 text-[14px] text-white placeholder:text-mute transition-colors"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              <CatChip active={cat === "all"} onClick={() => setCat("all")} label="All" />
              {BLOG_CATEGORIES.map((c) => (
                <CatChip
                  key={c.slug}
                  active={cat === c.slug}
                  onClick={() => setCat(c.slug)}
                  label={c.label}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 lg:py-24">
        {filtered.length === 0 ? (
          <p className="text-mute text-center py-24">No articles match your search.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((a, i) => (
              <ArticleCard key={a.slug} a={a} index={i} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

function CatChip({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      data-testid={`blog-cat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`whitespace-nowrap px-4 py-2 rounded-full text-[12px] tracking-wide border transition-colors ${
        active
          ? "bg-gold/15 border-gold/40 text-amber-100"
          : "border-line text-dim hover:border-gold/40 hover:text-white"
      }`}
    >
      {label}
    </button>
  );
}

function ArticleCard({ a, index }) {
  const cat = BLOG_CATEGORIES.find((c) => c.slug === a.category)?.label || a.category;
  // Generate a soft gradient seed per index for visual variety (no images)
  const gradients = [
    "from-amber-400/20 via-amber-500/10 to-transparent",
    "from-violet-400/20 via-fuchsia-500/10 to-transparent",
    "from-cyan-400/20 via-teal-500/10 to-transparent",
    "from-rose-400/15 via-amber-500/10 to-transparent",
  ];
  const g = gradients[index % gradients.length];

  return (
    <Link
      to={`/blog/${a.slug}`}
      data-testid={`blog-card-${a.slug}`}
      className="group relative rounded-2xl border border-line bg-[var(--gx-surface)]/40 backdrop-blur-md overflow-hidden hover:border-gold/40 transition-all hover:-translate-y-1 duration-300"
    >
      {/* graphic header */}
      <div className={`relative h-44 bg-gradient-to-br ${g} border-b border-line overflow-hidden`}>
        {a.image ? (
          <img src={a.image} alt={a.title} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        )}
        <div className="absolute bottom-4 left-4 text-[10px] tracking-[0.32em] uppercase text-amber-100/80 bg-black/30 backdrop-blur-md border border-line/60 px-3 py-1 rounded-full">
          {cat}
        </div>
        <Sparkles className="absolute top-4 right-4 w-4 h-4 text-gold/70" />
      </div>

      <div className="p-6">
        <h3 className="font-display text-xl tracking-tight leading-snug group-hover:text-gold transition-colors">
          {a.title}
        </h3>
        <p className="text-dim text-[13.5px] mt-3 leading-relaxed line-clamp-3">{a.excerpt}</p>
        <div className="flex items-center gap-4 mt-5 text-[11.5px] text-mute">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-3 h-3" /> {a.date}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> {a.read}
          </span>
        </div>
      </div>
    </Link>
  );
}

/**
 * BlogArticlePage — /blog/:slug
 */
export function BlogArticlePage({ onBook }) {
  const { slug } = useParams();
  const allArticles = useAllArticles();
  const article = allArticles.find((a) => a.slug === slug);

  // Build JSON-LD: Article (+ optional FAQPage from sections) + Breadcrumb.
  // Memoised so we don't churn the head on every render.
  const jsonLd = useMemo(() => {
    if (!article) return undefined;
    const url = `https://geniex.in/blog/${article.slug}`;
    // Construct an ISO date from "Feb 2026" → 2026-02-01.
    const MONTHS = { Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06", Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12" };
    const [mon, yr] = (article.date || "Feb 2026").split(" ");
    const iso = `${yr || "2026"}-${MONTHS[mon] || "02"}-01`;
    const sectionLabel = BLOG_CATEGORIES.find((c) => c.slug === article.category)?.label || article.category;
    const body = (article.sections || []).map((s) => `${s.heading}. ${s.body}`).join(" \n\n");

    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      headline: article.title.slice(0, 110),
      description: article.excerpt,
      articleSection: sectionLabel,
      keywords: article.keywords || undefined,
      inLanguage: "en-IN",
      url,
      datePublished: iso,
      dateModified: iso,
      wordCount: body.split(/\s+/).length,
      timeRequired: `PT${parseInt(article.read, 10) || 6}M`,
      image: ["https://geniex.in/renders/dynamic-hero.png"],
      author: { "@type": "Organization", name: "GenieX", url: "https://geniex.in" },
      publisher: {
        "@type": "Organization",
        name: "GenieX",
        url: "https://geniex.in",
        logo: { "@type": "ImageObject", url: "https://geniex.in/logo.png" },
      },
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://geniex.in/" },
        { "@type": "ListItem", position: 2, name: "Journal", item: "https://geniex.in/blog" },
        { "@type": "ListItem", position: 3, name: sectionLabel, item: `https://geniex.in/blog/category/${article.category}` },
        { "@type": "ListItem", position: 4, name: article.title, item: url },
      ],
    };

    // If the article has Q&A-style sections (heading is a question), surface
    // them as a FAQPage for the rich Q&A result on Google.
    const qa = (article.sections || []).filter(
      (s) => typeof s.heading === "string" && s.heading.trim().endsWith("?")
    );
    const faqSchema = qa.length >= 2 ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: qa.map((s) => ({
        "@type": "Question",
        name: s.heading,
        acceptedAnswer: { "@type": "Answer", text: s.body },
      })),
    } : null;

    return [articleSchema, breadcrumbSchema, faqSchema].filter(Boolean);
  }, [article]);

  useSEO({
    title: article ? article.title : "Article",
    description: article?.excerpt,
    path: `/blog/${slug}`,
    keywords: article?.keywords,
    jsonLd,
  });

  if (!article) {
    return (
      <main className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6 pt-32">
        <p className="eyebrow mb-3">Article not found</p>
        <Link to="/blog" className="btn-outline mt-8">Back to journal</Link>
      </main>
    );
  }

  const cat = BLOG_CATEGORIES.find((c) => c.slug === article.category)?.label || article.category;
  const related = allArticles.filter((a) => a.category === article.category && a.slug !== article.slug).slice(0, 3);

  return (
    <main data-testid={`blog-article-${slug}`} className="bg-[var(--gx-bg)] text-white">
      <article className="max-w-[820px] mx-auto px-5 lg:px-8 pt-28 lg:pt-36 pb-16">
        <Link to="/blog" className="inline-flex items-center gap-2 text-[12px] text-dim hover:text-gold transition-colors mb-10">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Journal
        </Link>
        <p className="eyebrow mb-4">{cat}</p>
        <h1 className="font-display tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.08] max-w-[24ch]">
          {article.title}
        </h1>
        <div className="flex items-center gap-5 mt-6 text-[12px] text-mute">
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {article.date}</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="w-3 h-3" /> {article.read}</span>
        </div>
        <div className={`relative h-48 mt-12 rounded-2xl bg-gradient-to-br from-amber-400/15 via-amber-500/5 to-transparent border border-line overflow-hidden`}>
          {article.image ? (
            <img src={article.image} alt={article.title} className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <>
              <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
              <Sparkles className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 text-gold/70" />
            </>
          )}
        </div>

        <div className="prose-gx mt-14 space-y-10">
          {article.sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-display text-xl sm:text-2xl tracking-tight text-white mb-4">{s.heading}</h2>
              <p className="text-dim text-[15.5px] leading-[1.85]">{renderRichText(s.body)}</p>
            </section>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-2xl border border-gold/30 bg-gradient-to-br from-gold/10 via-transparent to-transparent p-8">
          <p className="eyebrow mb-3">Begin your journey</p>
          <h3 className="font-display text-2xl tracking-tight max-w-[24ch]">
            Ready to design <span className="text-gold">your intelligent home?</span>
          </h3>
          <p className="text-dim text-[14px] mt-3 max-w-[52ch] leading-relaxed">
            Speak with a senior GenieX advisor — private demo, pricing in ₹, and a tailored plan for your home.
          </p>
          <button onClick={onBook} data-testid="article-cta" className="btn-gold mt-6">
            Book a Consultation <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="border-t border-line/70">
          <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 lg:py-20">
            <p className="eyebrow mb-3">Also in this category</p>
            <h2 className="font-display text-2xl sm:text-3xl tracking-tight">More from {cat}.</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
              {related.map((r, i) => <ArticleCard key={r.slug} a={r} index={i} />)}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
